import { debounce } from "lodash";
import axios from "axios";
import axiosInstance, { BASE_API } from "../axios";
import { CENTER_RADIOLOGY_URL, OPEN_PROSTATE_HP, REPORT_HEADER_FONT_SIZE } from "../constants/APP_CONSTANTS";
import { TokenStorage, UserStorage } from './cookieStorage';
import { FieldLabels } from '../constants/fieldLabels';
const moment = require('moment');

export const RADIOLOGY_URL = (pin, location) => {
  const radUrl = CENTER_RADIOLOGY_URL;
  return `${radUrl}?Pin=${pin}&LOC=${location}`;
};

export const ConvertStringToDate = (dateString, timeString) => {
  if (!dateString || !timeString) return '';
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6) - 1; // Month is zero-based in JavaScript Date objects
  const day = dateString.substring(6, 8);

  // Extract hour, minute, and second from the time string
  const hour = timeString.substring(0, 2);
  const minute = timeString.substring(2, 4);
  const second = timeString.substring(4, 6);

  // Create a Date object
  const combinedDate = new Date(year, month, day, hour, minute, second);
  return combinedDate;
};

export const setUserDetails = userDetails => {
  return UserStorage.setUserDetails(userDetails);
};

export const getUserDetails = () => {
  return UserStorage.getUserDetails();
};

export const hasPermission = permissionCode => {
  const userDetails = getUserDetails();
  const permissions = userDetails?.permissions || [];
  return permissions.some(p => p.up_key === permissionCode);
};

export const setAccessToken = (accessToken, expiresIn = 3600) => {
  return TokenStorage.setAccessToken(accessToken, expiresIn);
};

export const getAccessToken = () => {
  return TokenStorage.getAccessToken();
};

export const isTokenValid = () => {
  return TokenStorage.isTokenValid();
};

export const clearAuthData = () => {
  TokenStorage.clearAllTokens();
  UserStorage.removeUserDetails();
};

export const makePostCall = (url, payload, headers) => {
  return axiosInstance.post(BASE_API + url, payload, headers);
};

export const makeGetCall = (url, headers = {}) => {
  return axiosInstance.get(BASE_API + url, headers);
};

export const makePutCall = (url, payload, headers) => {
  return axiosInstance.put(BASE_API + url, payload, headers);
};

export const makeDeleteCall = (url, headers = {}) => {
  return axiosInstance.delete(BASE_API + url, headers);
};

export const makeGetCallForDownload = async url => {
  try {
    // Validate input
    if (!url || typeof url !== 'string') {
      throw new Error('Invalid download URL provided');
    }

    // Check authentication
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }

    // Create a new axios instance with the same base config but different baseURL for downloads
    const downloadInstance = axios.create({
      baseURL: BASE_API, // Use BASE_API directly, not BASE_URL
      timeout: 60000, // 60 second timeout for large downloads
    });

    // Add request interceptor for authentication
    downloadInstance.interceptors.request.use(
      function (config) {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for better error handling
    downloadInstance.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        if (error.response) {
          // Server responded with error status
          switch (error.response.status) {
            case 401:
              throw new Error('Session expired. Please log in again.');
            case 403:
              throw new Error('You do not have permission to download this file.');
            case 404:
              throw new Error('File not found. The deployment files may not be available.');
            case 500:
              throw new Error('Server error occurred while preparing the download.');
            default:
              throw new Error(`Download failed: ${error.response.statusText || 'Unknown error'}`);
          }
        } else if (error.request) {
          // Network error
          throw new Error('Network error. Please check your connection and try again.');
        } else {
          // Other error
          throw new Error('An unexpected error occurred during download.');
        }
      }
    );

    const response = await downloadInstance.get(url, {
      responseType: 'blob',
    });

    // Validate response
    if (!response.data || response.data.size === 0) {
      throw new Error('Downloaded file is empty or corrupted.');
    }

    return response;
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

export const uploadDicomStudies = async (files, onUploadProgress) => {
  try {
    // Validate input
    if (!files || files.length === 0) {
      throw new Error('No files provided for upload');
    }

    // Check authentication
    const token = getAccessToken();
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }

    // Create FormData and append files
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Create axios instance for upload with longer timeout
    const uploadInstance = axios.create({
      baseURL: BASE_API,
      timeout: 300000, // 5 minutes timeout for large uploads
    });

    // Add request interceptor for authentication
    uploadInstance.interceptors.request.use(
      function (config) {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );

    // Upload with progress tracking
    const response = await uploadInstance.post('/studies/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onUploadProgress,
    });

    return response.data;
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
};

const debouncedApiCall = debounce(payload => {
  makePostCall('/save-viewer-interactions', payload)
    .then(res => {})
    .catch(e => {
      console.error('Error', e);
    });
}, 300); // 300ms delay

export const saveViewerInteractions = props => {
  // Check if this window is in shared study mode
  if (window.isSharedStudyMode) {
    return;
  }

  const { study_id, series_id, tool_name, tool_action, viewport_index, additional_data } = props;
  const StudyInstanceUID = window.currentStudy;
  const SeriesInstanceUID = window.currentSeries;

  try {
    const user_id = getUserDetails()?.username;
    const payload = {
      user_id,
      study_id: StudyInstanceUID,
      series_id: SeriesInstanceUID,
      tool_name,
      tool_action,
      viewport_index,
      additional_data,
    };

    debouncedApiCall(payload);
  } catch (e) {
    console.error('Something went wrong while saving interactions', e);
  }
};

export const hisStatusOptions = [
  { label: 'UNCONFIRMED', value: 'UNCONFIRMED' },
  { label: 'CONFIRMATION_IN_PROGRESS', value: 'CONFIRMATION_IN_PROGRESS' },
  { label: 'WAIT_FOR_MANUAL_CONFIRMATION', value: 'WAIT_FOR_MANUAL_CONFIRMATION' },
  { label: 'CONFIRMED', value: 'CONFIRMED' },
  { label: 'DUPLICATE_ACCESSION_NO', value: 'DUPLICATE_ACCESSION_NO' },
  { label: 'CONFIRMATION_NOT_REQUIRED', value: 'CONFIRMATION_NOT_REQUIRED' },
];

export function calculateExactAge(dobString) {
  // Parse the DOB in YYYYMMDD format
  const dob = moment(dobString, 'YYYYMMDD');

  // If the DOB is invalid, return an error message
  if (!dob.isValid()) {
    return null;
  }

  // Check if the date is in the future
  if (dob.isAfter(moment())) {
    return null;
  }

  // Calculate difference in years, months, and days
  const now = moment();
  const years = Math.floor(moment.duration(now.diff(dob)).asYears());
  const months = Math.floor(moment.duration(now.diff(dob)).asMonths() % 12);
  const days = Math.floor(moment.duration(now.diff(dob)).asDays() % 30.44);

  // Determine the most appropriate unit to display
  if (years > 0) {
    return `${years} ${years === 1 ? 'year' : 'years'}`;
  } else if (months > 0) {
    return `${months} ${months === 1 ? 'month' : 'months'}`;
  } else {
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }
}

export const removeContentById = (html, idToRemove) => {
  // Create a temporary DOM element
  const temp = document.createElement('div');
  temp.innerHTML = html;
  // Find and remove the element with the specified ID
  const elementToRemove = temp.querySelector(`#${idToRemove}`);
  if (elementToRemove) {
    elementToRemove.remove();
  }

  // Return the modified HTML string
  return temp.innerHTML;
};

export const hasReportingPermission = userDetails => {
  const userType = userDetails?.user_type;
  if (userType === 'radiologist' || userType === 'typist' || userType === 'hod') {
    // additionally check explicitly if the user has the particular permission
    return true;
  }
  return false;
};

export const hasStudyViewingPermission = userDetails => {
  const userType = userDetails?.user_type;
  if (userType === 'radiologist' || userType === 'hod' || userType === 'technician') {
    // additionally check explicitly if the user has the particular permission
    return true;
  }
  return false;
};

export const hasUploadNotesPermission = userDetails => {
  const userType = userDetails?.user_type;
  if (userType === 'technician') {
    // additionally check explicitly if the user has the particular permission
    return true;
  }
  return false;
};

export const createTabChannel = (channelName = 'tab_communication') => {
  const channel = new BroadcastChannel(channelName);

  return {
    // Send URL update to other tabs
    sendUrlUpdate: newUrl => {
      channel.postMessage({ type: 'URL_UPDATE', url: newUrl });
    },

    // Listen for URL updates in the other tab
    listenForUpdates: callback => {
      channel.onmessage = event => {
        if (event.data.type === 'URL_UPDATE') {
          callback(event.data.url);
        }
      };
    },

    // Clean up when no longer needed
    cleanup: () => {
      channel.close();
    },
  };
};

// Convert base64 image to RTF hex format
const base64ToHex = base64String => {
  try {
    // Remove data URL prefix if present
    const base64Data = base64String.replace(/^data:image\/[a-z]+;base64,/, '');

    // Convert base64 to binary
    const binaryString = atob(base64Data);

    // Convert binary to hex
    let hex = '';
    for (let i = 0; i < binaryString.length; i++) {
      const hexChar = binaryString.charCodeAt(i).toString(16).padStart(2, '0');
      hex += hexChar;
    }

    return hex.toUpperCase();
  } catch (error) {
    console.error('Error converting base64 to hex:', error);
    return '';
  }
};

// Convert image to RTF format
const convertImageToRTF = imgElement => {
  try {
    const src = imgElement.getAttribute('src');
    if (!src || !src.startsWith('data:image/')) {
      return '[IMAGE: External image not supported in RTF]';
    }

    // Get image format
    const formatMatch = src.match(/^data:image\/([a-z]+);base64,/);
    const format = formatMatch ? formatMatch[1].toLowerCase() : 'png';

    // RTF image format mapping
    const rtfFormat = format === 'jpeg' || format === 'jpg' ? 'jpegblip' : 'pngblip';

    // Get image dimensions (default if not specified)
    const width = imgElement.getAttribute('width') || '200';
    const height = imgElement.getAttribute('height') || '100';

    // Convert to twips (RTF units: 1 inch = 1440 twips, assuming 96 DPI)
    const widthTwips = Math.round(parseInt(width) * 15); // Rough conversion
    const heightTwips = Math.round(parseInt(height) * 15);

    // Convert base64 to hex
    const hexData = base64ToHex(src);

    if (!hexData) {
      return '[IMAGE: Conversion failed]';
    }

    // Create RTF image structure
    const rtfImage = `{\\pict\\${rtfFormat}\\picw${widthTwips}\\pich${heightTwips}\\picwgoal${widthTwips}\\pichgoal${heightTwips} ${hexData}}`;

    return rtfImage;
  } catch (error) {
    console.error('Error converting image to RTF:', error);
    return '[IMAGE: Conversion error]';
  }
};

export const htmlToRtf = (htmlContent, patientData) => {
  // Start with proper RTF header
  let rtf =
    '{\\rtf1\\ansi\\ansicpg1252\\deff0 {\\fonttbl {\\f0\\fnil\\fcharset0 Times New Roman;}}\\f0\\fs24';

  // Clean and convert HTML content
  let content = htmlContent || '';

  // Remove "Loading editor..." if present
  if (content.includes('Loading editor...')) {
    content = '';
  }

  // Step by step conversion with logging
  let processedContent = content;

  // First, handle alignment with more comprehensive patterns
  const alignmentReplacements = [
    // CSS style alignment (most common) - Use [\s\S]*? to match across lines
    {
      pattern: /<p[^>]*style="[^"]*text-align:\s*center[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\qc $1\\par\\ql\\par',
    },
    {
      pattern: /<p[^>]*style="[^"]*text-align:\s*right[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\qr $1\\par\\ql\\par',
    },
    {
      pattern: /<p[^>]*style="[^"]*text-align:\s*left[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\ql $1\\par\\par',
    },

    // Div alignment
    {
      pattern: /<div[^>]*style="[^"]*text-align:\s*center[^"]*"[^>]*>([\s\S]*?)<\/div>/gi,
      replacement: '\\qc $1\\par\\ql',
    },
    {
      pattern: /<div[^>]*style="[^"]*text-align:\s*right[^"]*"[^>]*>([\s\S]*?)<\/div>/gi,
      replacement: '\\qr $1\\par\\ql',
    },
    {
      pattern: /<div[^>]*style="[^"]*text-align:\s*left[^"]*"[^>]*>([\s\S]*?)<\/div>/gi,
      replacement: '\\ql $1\\par',
    },

    // Jodit specific classes
    {
      pattern: /<p[^>]*class="[^"]*jodit_text_align_center[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\qc $1\\par\\ql\\par',
    },
    {
      pattern: /<p[^>]*class="[^"]*jodit_text_align_right[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\qr $1\\par\\ql\\par',
    },
    {
      pattern: /<p[^>]*class="[^"]*jodit_text_align_left[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\ql $1\\par\\par',
    },

    // Alternative class naming
    {
      pattern: /<p[^>]*class="[^"]*text-center[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\qc $1\\par\\ql\\par',
    },
    {
      pattern: /<p[^>]*class="[^"]*text-right[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\qr $1\\par\\ql\\par',
    },
    {
      pattern: /<p[^>]*class="[^"]*text-left[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\ql $1\\par\\par',
    },

    // Center tag (legacy)
    { pattern: /<center[^>]*>([\s\S]*?)<\/center>/gi, replacement: '\\qc $1\\par\\ql' },

    // Align attribute (legacy)
    {
      pattern: /<p[^>]*align="center"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\qc $1\\par\\ql\\par',
    },
    {
      pattern: /<p[^>]*align="right"[^>]*>([\s\S]*?)<\/p>/gi,
      replacement: '\\qr $1\\par\\ql\\par',
    },
    { pattern: /<p[^>]*align="left"[^>]*>([\s\S]*?)<\/p>/gi, replacement: '\\ql $1\\par\\par' },
    {
      pattern: /<div[^>]*align="center"[^>]*>([\s\S]*?)<\/div>/gi,
      replacement: '\\qc $1\\par\\ql',
    },
    { pattern: /<div[^>]*align="right"[^>]*>([\s\S]*?)<\/div>/gi, replacement: '\\qr $1\\par\\ql' },
    { pattern: /<div[^>]*align="left"[^>]*>([\s\S]*?)<\/div>/gi, replacement: '\\ql $1\\par' },
  ];

  // Apply alignment replacements
  alignmentReplacements.forEach((replacement, index) => {
    const before = processedContent;
    processedContent = processedContent.replace(replacement.pattern, replacement.replacement);
    if (before !== processedContent) {
    }
  });

  // Handle images before removing other tags
  processedContent = processedContent.replace(/<img[^>]*>/gi, imgTag => {
    // Create a temporary img element to parse attributes
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = imgTag;
    const imgElement = tempDiv.querySelector('img');

    if (imgElement) {
      return convertImageToRTF(imgElement);
    }
    return '[IMAGE: Parse error]';
  });

  // Now handle <br> tags within aligned content AFTER alignment is applied
  // Convert <br> to \line within aligned blocks to maintain alignment
  processedContent = processedContent.replace(/<br\s*\/?>/gi, '\\line'); // Convert all remaining <br> to \line first

  // Then handle other formatting
  processedContent = processedContent
    // Handle standard paragraph and div tags (for non-aligned content)
    .replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, '$1\\par\\par')
    .replace(/<div[^>]*>([\s\S]*?)<\/div>/gi, '$1\\par')

    // Handle text formatting
    .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '\\b $1\\b0')
    .replace(/<b[^>]*>(.*?)<\/b>/gi, '\\b $1\\b0')
    .replace(/<em[^>]*>(.*?)<\/em>/gi, '\\i $1\\i0')
    .replace(/<i[^>]*>(.*?)<\/i>/gi, '\\i $1\\i0')
    .replace(/<u[^>]*>(.*?)<\/u>/gi, '\\ul $1\\ulnone')

    // Handle span elements with styles
    .replace(
      /<span[^>]*style="[^"]*text-decoration:\s*underline[^"]*"[^>]*>(.*?)<\/span>/gi,
      '\\ul $1\\ulnone'
    )
    .replace(/<span[^>]*style="[^"]*font-weight:\s*bold[^"]*"[^>]*>(.*?)<\/span>/gi, '\\b $1\\b0')
    .replace(/<span[^>]*style="[^"]*font-style:\s*italic[^"]*"[^>]*>(.*?)<\/span>/gi, '\\i $1\\i0')
    .replace(/<span[^>]*style="[^"]*color:\s*[^;"]*[^"]*"[^>]*>(.*?)<\/span>/gi, '$1') // Remove color spans but keep content

    // Handle headings
    .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, '\\b\\fs28 $1\\b0\\fs24\\par')

    // Handle lists
    .replace(/<ul[^>]*>/gi, '')
    .replace(/<\/ul>/gi, '\\par')
    .replace(/<ol[^>]*>/gi, '')
    .replace(/<\/ol>/gi, '\\par')
    .replace(/<li[^>]*>(.*?)<\/li>/gi, '\\tab • $1\\par')

    // Remove remaining HTML tags but preserve content
    .replace(/<[^>]*>/g, '')

    // Handle HTML entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")

    // Clean up extra spaces and line breaks
    .replace(/\n\s*\n/g, '\\par')
    .replace(/\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Add content if exists
  if (processedContent && processedContent.trim() && processedContent !== ' ') {
    rtf += processedContent;
  } else {
    rtf += '\\i No report content entered.\\i0';
  }

  rtf += '\\par}';
  return rtf;
};

export function isValidDate(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  // Check if it's in YYYYMMDD format (8 digits)
  if (/^\d{8}$/.test(dateString)) {
    const year = parseInt(dateString.substring(0, 4), 10);
    const month = parseInt(dateString.substring(4, 6), 10);
    const day = parseInt(dateString.substring(6, 8), 10);

    // Validate ranges
    if (year < 1900 || year > 2100) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    // Create date and verify it's valid (handles leap years, month lengths)
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  }

  // Fallback to standard Date parsing for other formats
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
}

const getAgeFromDOB = dobString => {
  if (!isValidDate(dobString)) {
    return '-';
  }
  // Parse the date string in YYYYMMDD format
  const year = parseInt(dobString.substring(0, 4), 10);
  const month = parseInt(dobString.substring(4, 6), 10) - 1; // Months are 0-based in JavaScript Date
  const day = parseInt(dobString.substring(6, 8), 10);

  // Create a date object for the DOB
  const dob = new Date(year, month, day);

  // Get today's date
  const today = new Date();

  // Calculate the difference in time
  const diffInMilliseconds = today - dob;

  // If the difference is negative, return an error
  if (diffInMilliseconds < 0) {
    return 'Invalid DOB: Future date provided.';
  }

  // Calculate total days
  const totalDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));

  // Return age in days if less than a month
  if (totalDays < 30) {
    return `${totalDays} days`;
  }

  // Calculate total months
  const totalMonths =
    (today.getFullYear() - dob.getFullYear()) * 12 +
    today.getMonth() -
    dob.getMonth() -
    (today.getDate() < dob.getDate() ? 1 : 0);

  // Return age in months if less than a year
  if (totalMonths < 12) {
    return `${totalMonths} months`;
  }

  // Calculate years
  let years = today.getFullYear() - dob.getFullYear();
  if (
    today.getMonth() < dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
  ) {
    years--;
  }

  return `${years} years`;
};

/**
 * Maps gender shorthand to full gender name
 * @param {string} gender - Gender shorthand (M/F or m/f)
 * @returns {string} - Full gender name (Male/Female) or original value if not M/F
 */
export const mapGender = gender => {
  if (!gender) return '-';

  const upperGender = gender.toString().toUpperCase().trim();

  if (upperGender === 'M') {
    return 'Male';
  } else if (upperGender === 'F') {
    return 'Female';
  }

  // Return original value if not M or F
  return gender;
};

// Cache for header configuration to avoid async issues
let cachedHeaderConfig = null;
let headerConfigLoadAttempted = false;

// Load header configuration into cache (should be called at app initialization)
export const loadHeaderConfigCache = async () => {
  if (headerConfigLoadAttempted) return;
  headerConfigLoadAttempted = true;

  try {
    const response = await makePostCall('/no-auth/report-header-config', { action: 'get' });
    if (response?.data?.success && response?.data?.config) {
      cachedHeaderConfig = response.data.config;
    }
  } catch (error) {
    // Silently fail - will use default config
  }
};

// Clear cache when config is updated
export const clearHeaderConfigCache = () => {
  cachedHeaderConfig = null;
  headerConfigLoadAttempted = false;
};

export const PatientHeader = (patDetails, currentReport, orderDetails, radiologistName = null) => {
  // Try to use the new configurable header system if config is available
  if (cachedHeaderConfig) {
    try {
      // Import the generator function dynamically
      import('./reportHeaderGenerator')
        .then(module => {
          // Generator will be available for next render
        })
        .catch(() => {
          // Silently fail and use legacy
        });

      // Use the cached config with inline generation (to avoid async issues)
      const calculatedAge = getAgeFromDOB(patDetails.pat_dob);
      const age = calculatedAge && calculatedAge !== '-' ? calculatedAge : patDetails.pat_age;
      const mappedGender = mapGender(patDetails.pat_sex);

      // Generate rows based on configuration
      const rowsHtml = cachedHeaderConfig.rows
        .map(row => {
          // Filter out fields with null field identifiers only (keep empty data fields to show "-")
          const validFields = row.filter(cell => {
            const { field } = cell;
            // Only skip if field identifier is null or undefined
            return field && field !== null && field !== '';
          });

          // Skip this row if no valid fields
          if (validFields.length === 0) {
            return '';
          }

          // If only one valid field, make it span the full row
          if (validFields.length === 1) {
            const { label, field } = validFields[0];
            let value = '';

            // Get the value again for display
            switch (field) {
              case 'pat_name':
                value = patDetails.pat_name || '-';
                break;
              case 'pat_sex_age':
                value = `${mappedGender} / ${age}`;
                break;
              case 'pat_sex':
                value = mappedGender;
                break;
              case 'pat_age':
                value = age;
                break;
              case 'pat_pin':
                value = patDetails.pat_pin || '-';
                break;
              case 'po_acc_no':
                value = orderDetails.po_acc_no || '-';
                break;
              case 'po_ref_doc':
                value = orderDetails.po_ref_doc || '-';
                break;
              case 'po_modality':
                value = orderDetails.po_modality || '-';
                break;
              case 'po_req_time':
                value = orderDetails.po_req_time
                  ? moment(orderDetails.po_req_time).format('DD/MM/YYYY hh:mmA')
                  : '-';
                break;
              case 'report_created_at':
                value = currentReport?.created_at
                  ? moment(currentReport.created_at).format('DD/MM/YYYY hh:mmA')
                  : '-';
                break;
              case 'po_order_no':
                value = orderDetails.pacs_ord_id || '-';
                break;
              case 'pat_admission_no':
                value = patDetails.pat_admission_no || '-';
                break;
              case 'po_diag_desc':
                value = orderDetails.po_diag_desc || '-';
                break;
              case 'po_clinical_history':
                value = orderDetails.po_clinical_history || '-';
                break;
              case 'report_status':
                value = currentReport?.pr_status || '-';
                break;
              case 'assigned_radiologist':
                // Show empty initially, will be populated on signoff
                value = '-';
                break;
              default:
                value = '-';
            }

            // Determine if this field should be non-editable (Order Time, Report Time, and Radiologist)
            const isNonEditable = field === 'po_req_time' || field === 'report_created_at' || field === 'assigned_radiologist';
            const editableAttr = isNonEditable ? ' contenteditable="false"' : '';
            // Add IDs for value cells to enable reliable DOM access on signoff
            let idAttr = '';
            if (field === 'report_created_at') idAttr = ' id="report-datetime-value"';
            if (field === 'assigned_radiologist') idAttr = ' id="radiologist-value"';

            return `
            <div style="display: flex; border-bottom: 1px solid #000">
              <div style="flex: 1; display: flex; padding: 3px 6px">
                <div style="width: 17.5%; text-align: left; flex-shrink: 0">${label}</div>
                <div style="width: 2.5%; text-align: center; flex-shrink: 0">:</div>
                <div style="width: 80%; text-align: left; word-break: break-word; overflow-wrap: break-word; min-width: 0"${editableAttr}${idAttr}><strong>${value}</strong></div>
              </div>
            </div>
          `;
          }

          // Multiple valid fields - display them side by side
          const cellsHtml = validFields
            .map((cell, cellIndex) => {
              const { label, field } = cell;
              let value = '-';

              // Field value resolution
              switch (field) {
                case 'pat_name':
                  value = patDetails.pat_name || '-';
                  break;
                case 'pat_sex_age':
                  value = `${mappedGender} / ${age}`;
                  break;
                case 'pat_sex':
                  value = mappedGender;
                  break;
                case 'pat_age':
                  value = age;
                  break;
                case 'pat_pin':
                  value = patDetails.pat_pin || '-';
                  break;
                case 'po_acc_no':
                  value = orderDetails.po_acc_no || '-';
                  break;
                case 'po_ref_doc':
                  value = orderDetails.po_ref_doc || '-';
                  break;
                case 'po_modality':
                  value = orderDetails.po_modality || '-';
                  break;
                case 'po_req_time':
                  value = orderDetails.po_req_time
                    ? moment(orderDetails.po_req_time).format('DD/MM/YYYY hh:mmA')
                    : '-';
                  break;
                case 'report_created_at':
                  value = currentReport?.created_at
                    ? moment(currentReport.created_at).format('DD/MM/YYYY hh:mmA')
                    : '-';
                  break;
                case 'po_order_no':
                  value = orderDetails.pacs_ord_id || '-';
                  break;
                case 'pat_admission_no':
                  value = patDetails.pat_admission_no || '-';
                  break;
                case 'po_diag_desc':
                  value = orderDetails.po_diag_desc || '-';
                  break;
                case 'po_clinical_history':
                  value = orderDetails.po_clinical_history || '-';
                  break;
                case 'report_status':
                  value = currentReport?.pr_status || '-';
                  break;
                case 'assigned_radiologist':
                  // Show empty initially, will be populated on signoff
                  value = '-';
                  break;
                default:
                  value = '-';
              }

              // Determine if this field should be non-editable (Order Time, Report Time, and Radiologist)
              const isNonEditable = field === 'po_req_time' || field === 'report_created_at' || field === 'assigned_radiologist';
              const editableAttr = isNonEditable ? ' contenteditable="false"' : '';
              // Add IDs for value cells to enable reliable DOM access on signoff
              let idAttr = '';
              if (field === 'report_created_at') idAttr = ' id="report-datetime-value"';
              if (field === 'assigned_radiologist') idAttr = ' id="radiologist-value"';

              return `
            <div style="flex: 1; display: flex; padding: 3px 6px${cellIndex === 0 ? '; border-right: 1px solid #000' : ''}">
              <div style="width: 35%; text-align: left; flex-shrink: 0">${label}</div>
              <div style="width: 5%; text-align: center; flex-shrink: 0">:</div>
              <div style="width: 60%; text-align: left; word-break: break-word; overflow-wrap: break-word; min-width: 0"${editableAttr}${idAttr}><strong>${value}</strong></div>
            </div>
          `;
            })
            .join('');

          return `
          <div style="display: flex; border-bottom: 1px solid #000">
            ${cellsHtml}
          </div>
        `;
        })
        .filter(rowHtml => rowHtml !== '')
        .join(''); // Remove empty rows

      const fontSize = REPORT_HEADER_FONT_SIZE || 10;
      return `
        <div style="border: 1px solid #000; width: 100%; font-size: ${fontSize}px; margin: auto; padding: 0; max-height: 150px; overflow: hidden; display: flex; align-items: stretch;">
          <div style="flex: 1;">
            ${rowsHtml}
          </div>
        </div>
      `;
    } catch (error) {
      // Fall back to legacy implementation
    }
  }

  // Legacy implementation (fallback or when no config is available)
  const calculatedAge = getAgeFromDOB(patDetails.pat_dob);
  const age = calculatedAge && calculatedAge !== '-' ? calculatedAge : patDetails.pat_age;
  const mappedGender = mapGender(patDetails.pat_sex);
  const fontSize = REPORT_HEADER_FONT_SIZE || 10;
  return `

    <div style="border: 1px solid #000; width: 90%; font-size: ${fontSize}px; margin: auto; padding: 0; max-height: 150px; overflow: hidden; display: flex; align-items: stretch;">
      <div style="flex: 1;">
        <div style="display: flex; border-bottom: 1px solid #000">
          <div style="flex: 1; display: flex; padding: 3px 6px; border-right: 1px solid #000">
            <div style="width: 35%; text-align: left; flex-shrink: 0">${FieldLabels.PATIENT_NAME}</div>
            <div style="width: 5%; text-align: center; flex-shrink: 0">:</div>
            <div style="width: 60%; text-align: left; word-break: break-word; overflow-wrap: break-word; min-width: 0"><strong>${patDetails.pat_name}</strong></div>
          </div>
          <div style="flex: 1; display: flex; padding: 3px 6px">
            <div style="width: 35%; text-align: left; flex-shrink: 0">${FieldLabels.PATIENT_REPORT_SEX} / ${FieldLabels.PATIENT_REPORT_AGE}</div>
            <div style="width: 5%; text-align: center; flex-shrink: 0">:</div>
            <div style="width: 60%; text-align: left; word-break: break-word; overflow-wrap: break-word; min-width: 0"><strong>${mappedGender} / ${age}</strong></div>
          </div>
        </div>
        <div style="display: flex; border-bottom: 1px solid #000">
          <div style="flex: 1; display: flex; padding: 3px 6px; border-right: 1px solid #000">
            <div style="width: 35%; text-align: left; flex-shrink: 0">${FieldLabels.PATIENT_ID}</div>
            <div style="width: 5%; text-align: center; flex-shrink: 0">:</div>
            <div style="width: 60%; text-align: left; word-break: break-word; overflow-wrap: break-word; min-width: 0"><strong>${patDetails.pat_pin || '-'}</strong></div>
          </div>
          <div style="flex: 1; display: flex; padding: 3px 6px">
            <div style="width: 35%; text-align: left; flex-shrink: 0">${FieldLabels.ACCESSION_NUMBER_SHORTEST}</div>
            <div style="width: 5%; text-align: center; flex-shrink: 0">:</div>
            <div style="width: 60%; text-align: left; word-break: break-word; overflow-wrap: break-word; min-width: 0"><strong>${orderDetails.po_acc_no || '-'}</strong></div>
          </div>
        </div>
        <div style="display: flex; border-bottom: 1px solid #000">
          <div style="flex: 1; display: flex; padding: 3px 6px; border-right: 1px solid #000">
            <div style="width: 35%; text-align: left; flex-shrink: 0">${FieldLabels.REFERRING_DOCTOR}</div>
            <div style="width: 5%; text-align: center; flex-shrink: 0">:</div>
            <div style="width: 60%; text-align: left; word-break: break-word; overflow-wrap: break-word; min-width: 0"><strong>${orderDetails.po_ref_doc || '-'}</strong></div>
          </div>
          <div style="flex: 1; display: flex; padding: 3px 6px">
            <div style="width: 35%; text-align: left; flex-shrink: 0">${FieldLabels.MODALITY}</div>
            <div style="width: 5%; text-align: center; flex-shrink: 0">:</div>
            <div style="width: 60%; text-align: left; word-break: break-word; overflow-wrap: break-word; min-width: 0"><strong>${orderDetails.po_modality || '-'}</strong></div>
          </div>
        </div>
        <div style="display: flex; border-bottom: 1px solid #000">
          <div style="flex: 1; display: flex; padding: 3px 6px; border-right: 1px solid #000">
            <div style="width: 35%; text-align: left; flex-shrink: 0">Order Time</div>
            <div style="width: 5%; text-align: center; flex-shrink: 0">:</div>
            <div style="width: 60%; text-align: left; word-break: break-word; overflow-wrap: break-word; min-width: 0" contenteditable="false"><strong>${moment(orderDetails.po_req_time).format('DD/MM/YYYY hh:mmA')}</strong></div>
          </div>
          <div style="flex: 1; display: flex; padding: 3px 6px;">
            <div style="width: 35%; text-align: left; flex-shrink: 0">Report Time</div>
            <div style="width: 5%; text-align: center; flex-shrink: 0">:</div>
            <div style="width: 60%; text-align: left; word-break: break-word; overflow-wrap: break-word; min-width: 0" contenteditable="false" id="report-datetime-value"><strong>${currentReport?.created_at ? moment(currentReport?.created_at).format('DD/MM/YYYY hh:mmA') : '-'}</strong></div>
          </div>
        </div>
      </div>
  `;
};


export const getHangingProtocolByModalityAndBodyPart = (modality, bodyPart, studyDescription) => {
  if (modality === 'MR' && bodyPart?.toUpperCase().includes('PELVIS') && OPEN_PROSTATE_HP) {
    return "@ohif/hpProstatePIRADS";
  }
  return '';
}

export function delay(milliseconds) {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}


export const defaultMouseActions = {"leftClick":"WindowLevel","rightClick":"StackScroll","middleClick":"Pan","scroll":"StackScroll"};
